/**
 * <lead-capture-popup>: side tab + drawer panel inside a native <dialog>.
 *
 * The popup opens only when the shopper clicks the side tab. Open/close are animated
 * through a data-state attribute (closed -> open -> closing -> closed) so the CSS can
 * slide the panel out of the tab and back.
 *
 * Submit sends the lead to two destinations in parallel:
 * - RD Station Marketing conversions API (public token), with every field;
 * - Shopify `customer` form (email, first name, tags), only when the visitor opts in,
 *   because that form subscribes the customer to email marketing.
 * The sign-up counts as successful when at least one destination accepts it; the RD
 * Station Shopify app also syncs the customer, so either path reaches RD.
 *
 * Lifecycle is tied to connectedCallback/disconnectedCallback, so theme editor section
 * reloads tear everything down without leaking listeners or timers.
 */
(function () {
  if (customElements.get('lead-capture-popup')) return;

  var RD_ENDPOINT = 'https://api.rd.services/platform/conversions';
  var REQUEST_TIMEOUT_MS = 15000;
  var PHONE_MIN_DIGITS = 10;
  var PHONE_MAX_DIGITS = 13;
  var COPY_STATUS_MS = 4000;
  // Keep in sync with --lcp-exit and the .lcp-view.is-leaving transition in the CSS.
  var CLOSE_MS = 220;
  var VIEW_LEAVE_MS = 160;
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var FIELD_IDENTIFIER_PATTERN = /^[a-z0-9_]+$/i;
  var SAME_ORIGIN_PATH_PATTERN = /^\/(?!\/)/;
  // Swipe-down-to-dismiss for the mobile bottom sheet.
  var DESKTOP_QUERY = '(min-width: 768px)';
  var DRAG_START_PX = 8;
  var DISMISS_DISTANCE_RATIO = 0.25;
  var DISMISS_MAX_DISTANCE_PX = 140;
  var FLICK_MIN_DISTANCE_PX = 40;
  var FLICK_MIN_VELOCITY = 0.5; // px per ms
  var STORAGE_CAPTURED = 't4amLeadCapture:captured';
  var SUCCESS_EVENT = 'lead-capture:success';

  // Storage can throw (private mode, blocked cookies); the popup must still work.
  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      // Not persisted: the tab may show again next visit, which is acceptable.
    }
  }

  function readCookie(name) {
    var match = document.cookie.match('(?:^|; )' + name.replace(/\./g, '\\.') + '=([^;]*)');
    if (!match) return '';
    try {
      return decodeURIComponent(match[1]);
    } catch (error) {
      return match[1];
    }
  }

  function fieldValue(form, name) {
    var field = form.elements[name];
    return field ? String(field.value || '').trim() : '';
  }

  function isOptedIn(form) {
    return Boolean(form.elements.optin && form.elements.optin.checked);
  }

  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Two frames: the first commits the initial styles, the second starts the transition.
  // rAF is paused in background tabs, so a short timeout guarantees the callback still runs;
  // whichever fires first wins.
  var FRAME_FALLBACK_MS = 50;
  function nextFrame(callback) {
    var hasRun = false;
    function runOnce() {
      if (hasRun) return;
      hasRun = true;
      callback();
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(runOnce);
    });
    setTimeout(runOnce, FRAME_FALLBACK_MS);
  }

  function withTimeout(request) {
    var controller = 'AbortController' in window ? new AbortController() : null;
    var timer = controller
      ? setTimeout(function () {
          controller.abort();
        }, REQUEST_TIMEOUT_MS)
      : null;

    return request(controller ? controller.signal : undefined).finally(function () {
      if (timer) clearTimeout(timer);
    });
  }

  function buildRdBody(form, settings) {
    var payload = {
      conversion_identifier: settings.rdConversion,
      email: fieldValue(form, 'email').toLowerCase(),
      name: fieldValue(form, 'name')
    };
    var phone = fieldValue(form, 'mobile_phone');
    var birthdate = fieldValue(form, 'birthdate');
    var trafficSource = readCookie('__trf.src');

    if (phone) payload.mobile_phone = phone;
    if (birthdate && settings.rdBirthdateField) payload[settings.rdBirthdateField] = birthdate;
    if (trafficSource) payload.traffic_source = trafficSource;
    // Only send consent when given; never record a "declined" that could unsubscribe
    // someone who already opted in elsewhere.
    if (isOptedIn(form)) {
      payload.legal_bases = [{ category: 'communications', type: 'consent', status: 'granted' }];
    }

    return { event_type: 'CONVERSION', event_family: 'CDP', payload: payload };
  }

  function sendToRd(form, settings) {
    var body = JSON.stringify(buildRdBody(form, settings));
    return withTimeout(function (signal) {
      return fetch(RD_ENDPOINT + '?api_key=' + encodeURIComponent(settings.rdToken), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        signal: signal
      });
    }).then(function (response) {
      if (!response.ok) throw new Error('RD Station responded with status ' + response.status);
    });
  }

  function sendToShopify(form, settings) {
    var data = new FormData();
    var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    data.append('form_type', 'customer');
    data.append('utf8', '✓');
    data.append('contact[email]', fieldValue(form, 'email').toLowerCase());
    data.append('contact[first_name]', fieldValue(form, 'name'));
    if (settings.customerTags) data.append('contact[tags]', settings.customerTags);

    return withTimeout(function (signal) {
      return fetch(root + 'contact', { method: 'POST', body: data, signal: signal });
    }).then(function (response) {
      // Bot protection answers with a redirect to /challenge instead of an error status.
      if (!response.ok || /\/challenge/.test(response.url)) {
        throw new Error('Shopify customer form was not accepted');
      }
    });
  }

  class LeadCapturePopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-lcp-dialog]');
      this.panel = this.querySelector('[data-lcp-panel]');
      this.tab = this.querySelector('[data-lcp-open]');
      this.form = this.querySelector('[data-lcp-form]');
      this.formView = this.querySelector('[data-lcp-form-view]');
      this.successView = this.querySelector('[data-lcp-success]');
      this.errorBox = this.querySelector('[data-lcp-error]');
      this.submitButton = this.querySelector('[data-lcp-submit]');
      this.submitLabel = this.querySelector('[data-lcp-submit-label]');
      this.closeButton = this.querySelector('[data-lcp-close]');
      this.backdrop = this.querySelector('[data-lcp-backdrop]');
      this.copyButton = this.querySelector('[data-lcp-copy]');
      if (!this.dialog || !this.tab || !this.form || !this.formView || !this.successView || !this.errorBox) return;
      // Very old browsers without <dialog>: keep the tab hidden rather than show a dead button.
      if (typeof this.dialog.showModal !== 'function') return;

      var birthdateField = (this.dataset.rdBirthdateField || '').trim();
      this.settings = {
        rdToken: (this.dataset.rdToken || '').trim(),
        rdConversion: (this.dataset.rdConversion || '').trim(),
        rdBirthdateField: FIELD_IDENTIFIER_PATTERN.test(birthdateField) ? birthdateField : '',
        customerTags: (this.dataset.customerTags || '').trim(),
        phoneRequired: this.dataset.phoneRequired === 'true'
      };
      this.submitText = this.submitLabel ? this.submitLabel.textContent : '';
      this.isSending = false;
      this.wasCaptured = false;
      this.timers = [];
      this.drag = null;

      this.bindHandlers();
      this.addListeners();

      if (isDesignMode()) {
        this.initDesignMode();
        return;
      }
      if (storageGet(STORAGE_CAPTURED)) return;
      this.tab.hidden = false;
    }

    disconnectedCallback() {
      if (!this.handlers) return;
      this.timers.forEach(clearTimeout);
      this.timers = [];
      this.removeListeners();
      this.handlers = null;
      this.unlockScroll();
    }

    later(callback, ms) {
      var timer = setTimeout(callback, ms);
      this.timers.push(timer);
      return timer;
    }

    bindHandlers() {
      this.handlers = {
        open: this.open.bind(this),
        close: this.close.bind(this),
        onDialogCancel: this.onDialogCancel.bind(this),
        onDialogClose: this.onDialogClose.bind(this),
        onSubmit: this.onSubmit.bind(this),
        onFieldBlur: this.onFieldBlur.bind(this),
        onFieldInput: this.onFieldInput.bind(this),
        onCopy: this.onCopy.bind(this),
        onTouchStart: this.onTouchStart.bind(this),
        onTouchMove: this.onTouchMove.bind(this),
        onTouchEnd: this.onTouchEnd.bind(this),
        onSectionSelect: this.onSectionSelect.bind(this),
        onSectionDeselect: this.onSectionDeselect.bind(this)
      };
    }

    addListeners() {
      var h = this.handlers;
      this.tab.addEventListener('click', h.open);
      if (this.closeButton) this.closeButton.addEventListener('click', h.close);
      if (this.backdrop) this.backdrop.addEventListener('click', h.close);
      if (this.copyButton) this.copyButton.addEventListener('click', h.onCopy);
      this.dialog.addEventListener('cancel', h.onDialogCancel);
      this.dialog.addEventListener('close', h.onDialogClose);
      this.form.addEventListener('submit', h.onSubmit);
      this.form.addEventListener('focusout', h.onFieldBlur);
      this.form.addEventListener('input', h.onFieldInput);
      if (this.panel) {
        this.panel.addEventListener('touchstart', h.onTouchStart, { passive: true });
        // Not passive: a sheet drag must cancel the native scroll/rubber-band.
        this.panel.addEventListener('touchmove', h.onTouchMove, { passive: false });
        this.panel.addEventListener('touchend', h.onTouchEnd);
        this.panel.addEventListener('touchcancel', h.onTouchEnd);
      }
    }

    removeListeners() {
      var h = this.handlers;
      this.tab.removeEventListener('click', h.open);
      if (this.closeButton) this.closeButton.removeEventListener('click', h.close);
      if (this.backdrop) this.backdrop.removeEventListener('click', h.close);
      if (this.copyButton) this.copyButton.removeEventListener('click', h.onCopy);
      this.dialog.removeEventListener('cancel', h.onDialogCancel);
      this.dialog.removeEventListener('close', h.onDialogClose);
      this.form.removeEventListener('submit', h.onSubmit);
      this.form.removeEventListener('focusout', h.onFieldBlur);
      this.form.removeEventListener('input', h.onFieldInput);
      if (this.panel) {
        this.panel.removeEventListener('touchstart', h.onTouchStart);
        this.panel.removeEventListener('touchmove', h.onTouchMove);
        this.panel.removeEventListener('touchend', h.onTouchEnd);
        this.panel.removeEventListener('touchcancel', h.onTouchEnd);
      }
      document.removeEventListener('shopify:section:select', h.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', h.onSectionDeselect);
    }

    initDesignMode() {
      // In the editor the tab is always visible and the dialog follows section selection.
      this.tab.hidden = false;
      document.addEventListener('shopify:section:select', this.handlers.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.handlers.onSectionDeselect);
    }

    onSectionSelect(event) {
      if (event.detail && event.detail.sectionId === this.dataset.sectionId) this.open();
    }

    onSectionDeselect(event) {
      if (event.detail && event.detail.sectionId === this.dataset.sectionId) this.close();
    }

    setState(state) {
      this.dataset.state = state;
      this.dialog.dataset.state = state;
    }

    open(event) {
      if (this.dialog.open) return;
      // Safari does not focus buttons on click, so remember the tab explicitly.
      this.returnFocusTo = event && event.currentTarget === this.tab ? this.tab : document.activeElement;
      this.setState('opening');
      this.dialog.showModal();
      this.lockScroll();
      var self = this;
      nextFrame(function () {
        // Only advance if nothing closed the dialog in the meantime.
        if (self.dialog.open && self.dataset.state === 'opening') self.setState('open');
      });
    }

    // Every close path (button, backdrop, Esc, editor deselect) runs through here so the
    // exit animation and the cleanup do not depend on the native `close` event.
    close() {
      if (!this.dialog.open || this.dataset.state === 'closing') return;
      this.setState('closing');
      var self = this;
      this.later(
        function () {
          if (self.dialog.open) self.dialog.close();
          self.finishClose();
        },
        prefersReducedMotion() ? 0 : CLOSE_MS
      );
    }

    onDialogCancel(event) {
      // Esc: take over the native close so it animates and goes through the same cleanup.
      // Browsers may refuse the preventDefault (no recent user activation); onDialogClose
      // then catches the native close.
      event.preventDefault();
      this.close();
    }

    onDialogClose() {
      // Safety net for closes we did not start (e.g. a non-cancelable Esc).
      this.finishClose();
    }

    // Idempotent: may run from both the close timer and the native `close` event.
    finishClose() {
      if (this.dataset.state === 'closed') return;
      this.setState('closed');
      this.afterClose();
    }

    // Swipe down on the mobile sheet to dismiss it, like a native iOS sheet. The drag only
    // starts from a downward, mostly vertical pull while the sheet content is scrolled to
    // the top, so normal scrolling inside the form keeps working.
    onTouchStart(event) {
      this.drag = null;
      if (event.touches.length !== 1 || this.dataset.state !== 'open') return;
      if (this.panel.scrollTop > 0) return;
      if (window.matchMedia && window.matchMedia(DESKTOP_QUERY).matches) return;
      var touch = event.touches[0];
      this.drag = {
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: event.timeStamp,
        isDragging: false,
        distance: 0,
        height: 0
      };
    }

    onTouchMove(event) {
      var drag = this.drag;
      if (!drag) return;
      var touch = event.touches[0];
      var dx = touch.clientX - drag.startX;
      var dy = touch.clientY - drag.startY;

      if (!drag.isDragging) {
        if (Math.abs(dx) < DRAG_START_PX && Math.abs(dy) < DRAG_START_PX) return;
        if (dy <= 0 || Math.abs(dx) > dy) {
          this.drag = null;
          return;
        }
        drag.isDragging = true;
        drag.height = this.panel.offsetHeight || 1;
        this.panel.style.transition = 'none';
        if (this.backdrop) this.backdrop.style.transition = 'none';
      }

      event.preventDefault();
      drag.distance = Math.max(0, dy);
      this.panel.style.transform = 'translateY(' + drag.distance + 'px)';
      if (this.backdrop) this.backdrop.style.opacity = String(Math.max(0, 1 - drag.distance / drag.height));
    }

    onTouchEnd(event) {
      var drag = this.drag;
      this.drag = null;
      if (!drag || !drag.isDragging) return;

      var elapsed = Math.max(1, event.timeStamp - drag.startTime);
      var isFarEnough = drag.distance > Math.min(drag.height * DISMISS_DISTANCE_RATIO, DISMISS_MAX_DISTANCE_PX);
      var isFlick = drag.distance > FLICK_MIN_DISTANCE_PX && drag.distance / elapsed > FLICK_MIN_VELOCITY;
      var shouldClose = event.type === 'touchend' && (isFarEnough || isFlick);

      // Dropping the inline styles hands the panel back to the stylesheet, which animates
      // it from where the finger left it: down and out on close, back up otherwise.
      this.resetDragStyles();
      if (shouldClose) this.close();
    }

    resetDragStyles() {
      if (this.panel) {
        this.panel.style.removeProperty('transition');
        this.panel.style.removeProperty('transform');
      }
      if (this.backdrop) {
        this.backdrop.style.removeProperty('transition');
        this.backdrop.style.removeProperty('opacity');
      }
    }

    afterClose() {
      this.drag = null;
      this.resetDragStyles();
      this.unlockScroll();
      if (this.wasCaptured && !isDesignMode()) this.tab.hidden = true;
      var target = this.returnFocusTo;
      this.returnFocusTo = null;
      if (target && target !== document.body && target.isConnected && !target.hidden) target.focus();
    }

    lockScroll() {
      document.documentElement.classList.add('lcp-scroll-lock');
    }

    unlockScroll() {
      document.documentElement.classList.remove('lcp-scroll-lock');
    }

    fieldIsValid(name) {
      var value = fieldValue(this.form, name);
      if (name === 'name') return value.length > 1;
      if (name === 'email') return EMAIL_PATTERN.test(value);
      if (name === 'mobile_phone') {
        var digits = value.replace(/\D/g, '').length;
        if (digits === 0) return !this.settings.phoneRequired;
        return digits >= PHONE_MIN_DIGITS && digits <= PHONE_MAX_DIGITS;
      }
      return true;
    }

    setFieldState(name, isValid) {
      var field = this.form.elements[name];
      var message = this.form.querySelector('[data-lcp-field-error="' + name + '"]');
      if (!field) return;
      if (isValid) {
        field.removeAttribute('aria-invalid');
      } else {
        field.setAttribute('aria-invalid', 'true');
      }
      if (message) message.hidden = isValid;
    }

    validate() {
      var self = this;
      var firstInvalid = null;
      ['name', 'email', 'mobile_phone'].forEach(function (name) {
        var field = self.form.elements[name];
        if (!field) return;
        var isValid = self.fieldIsValid(name);
        self.setFieldState(name, isValid);
        if (!isValid && !firstInvalid) firstInvalid = field;
      });
      return firstInvalid;
    }

    onFieldBlur(event) {
      // Validate on blur once the shopper has typed something; empty untouched fields stay quiet.
      var field = event.target;
      if (!field || !field.name || field.name === 'optin' || field.name === 'hp_field') return;
      if (!fieldValue(this.form, field.name) && field.getAttribute('aria-invalid') !== 'true') return;
      this.setFieldState(field.name, this.fieldIsValid(field.name));
    }

    onFieldInput(event) {
      // Clear an error as soon as the shopper fixes the field.
      var field = event.target;
      if (field && field.getAttribute('aria-invalid') === 'true' && this.fieldIsValid(field.name)) {
        this.setFieldState(field.name, true);
      }
    }

    showError(message) {
      this.errorBox.textContent = message;
      this.errorBox.hidden = false;
    }

    setSending(isSending) {
      this.isSending = isSending;
      if (!this.submitButton) return;
      this.submitButton.disabled = isSending;
      this.submitButton.setAttribute('aria-busy', String(isSending));
      if (this.submitLabel) this.submitLabel.textContent = isSending ? this.dataset.msgSending : this.submitText;
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.isSending) return;
      this.errorBox.hidden = true;

      var firstInvalid = this.validate();
      if (firstInvalid) {
        this.showError(this.dataset.msgInvalid);
        firstInvalid.focus();
        return;
      }

      // Honeypot filled: pretend success so bots get no signal, but send nothing and
      // remember nothing. Timing is deliberately not used: autofill lets real shoppers
      // submit within a second, and dropping them would lose the lead and the consent.
      if (fieldValue(this.form, 'hp_field')) {
        this.showSuccess(false);
        return;
      }

      var requests = [];
      if (this.settings.rdToken && this.settings.rdConversion) requests.push(sendToRd(this.form, this.settings));
      if (isOptedIn(this.form)) requests.push(sendToShopify(this.form, this.settings));
      if (!requests.length) {
        this.showError(this.dataset.msgUnavailable);
        return;
      }

      var self = this;
      this.setSending(true);
      Promise.allSettled(requests).then(function (results) {
        self.setSending(false);
        var anyAccepted = results.some(function (result) {
          return result.status === 'fulfilled';
        });
        if (!anyAccepted) {
          self.showError(self.dataset.msgFailed);
          return;
        }
        self.form.reset();
        self.showSuccess(true);
      });
    }

    showSuccess(isGenuine) {
      this.updateApplyLink();
      this.swapToSuccess();
      if (!isGenuine) return;
      this.wasCaptured = true;
      storageSet(STORAGE_CAPTURED, String(Date.now()));
      this.dispatchEvent(
        new CustomEvent(SUCCESS_EVENT, {
          bubbles: true,
          detail: { conversionIdentifier: this.settings.rdConversion }
        })
      );
    }

    swapToSuccess() {
      // Form fades up and out, then the coupon view rises in.
      var self = this;
      var leaveMs = prefersReducedMotion() ? 0 : VIEW_LEAVE_MS;
      this.formView.classList.add('is-leaving');
      this.later(function () {
        self.formView.hidden = true;
        self.formView.classList.remove('is-leaving');
        self.successView.hidden = false;
        self.successView.classList.add('is-entering');
        self.successView.focus();
      }, leaveMs);
    }

    updateApplyLink() {
      // Path only: the query string may carry campaign params or PII. Reject "//host" paths.
      var link = this.querySelector('[data-lcp-apply]');
      var path = window.location.pathname;
      if (!link || !SAME_ORIGIN_PATH_PATTERN.test(path)) return;
      var url = new URL(link.getAttribute('href'), window.location.origin);
      url.searchParams.set('redirect', path);
      link.setAttribute('href', url.pathname + url.search);
    }

    onCopy() {
      var codeElement = this.querySelector('[data-lcp-coupon]');
      var status = this.querySelector('[data-lcp-copy-status]');
      if (!codeElement) return;
      var code = codeElement.textContent.trim();
      var self = this;

      function announce() {
        if (self.copyButton) self.copyButton.classList.add('is-copied');
        if (status) status.textContent = self.dataset.msgCopied;
        self.later(function () {
          if (self.copyButton) self.copyButton.classList.remove('is-copied');
          if (status) status.textContent = '';
        }, COPY_STATUS_MS);
      }

      function selectCode() {
        // Clipboard unavailable: select the code so the shopper can copy it manually.
        var range = document.createRange();
        var selection = window.getSelection();
        range.selectNodeContents(codeElement);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(announce, selectCode);
      } else {
        selectCode();
      }
    }
  }

  customElements.define('lead-capture-popup', LeadCapturePopup);
})();
